import { useCallback, useEffect } from 'react';

import { callSNOWDataService } from 'src/utils/snowDataServiceWrapper';
import { logger } from 'src/utils/logger';
import { getApiErrorMessage } from 'src/utils/CommonUtils';
import {
	KeyAnyValuePairsObject,
	KeyStringValuePairsObject,
} from 'src/@types/common';

import useStateObject from './useStateObject';

export interface useFetchState<T> {
	isLoading: boolean;
	data: T | null;
	error: string | null;
	fetchData?: () => void;
}

export interface useFetchForSDSRequestProps {
	path: string;
	headers?: KeyStringValuePairsObject;
	queryParams?: KeyAnyValuePairsObject;
	method?: string;
	body?: KeyAnyValuePairsObject;
	url?: string;
	credentials?: KeyStringValuePairsObject;
	region?: string;
}

export const useFetchForSDS = <T>(requestProps: useFetchForSDSRequestProps) => {
	const { path } = requestProps;
	const [result, setResult] = useStateObject<useFetchState<T>>({
		isLoading: false,
		data: null,
		error: null,
		fetchData: undefined,
	});
	const fetchData = useCallback(() => {
		const abortController = new AbortController();

		if (path !== '') {
			setResult({
				isLoading: true,
				data: null,
				error: null,
			});
			logger.info('useFetchForSDS hook called for API path:' + path);

			callSNOWDataService({
				...requestProps,
				signal: abortController.signal,
			} as never)
				.then((response) => {
					const contextToLog = {
						path,
						data: response,
					};

					logger.info(
						'useFetchForSDS hook returned successfully',
						contextToLog
					);
					setResult({
						isLoading: false,
						data: response,
						error: null,
					});
				})
				.catch((error) => {
					logger.error(
						'useFetchForSDS hook called for API path:' +
							path +
							' returned error' +
							JSON.stringify(error)
					);
					setResult({
						isLoading: false,
						data: null,
						error: getApiErrorMessage(error),
					});
				});
		}

		return () => {
			abortController.abort();
		};
	}, [path]);

	useEffect(fetchData, [fetchData]);

	return { ...result, fetchData };
};
